import React, { useEffect, useState, useRef } from "react";
import { TextField, Grid, Box } from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import { StatnoViewModel } from "../../../Types/ExportTypes";
import DefaultActions from "../../../components/Card/DefaultActions";
import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";

const StatnoEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { code = "" } = params || {};
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const { showLoading, hideLoading } = useLoading();
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const { navigate } = useNavigation();
    const [viewModel, setViewModel] = useState<StatnoViewModel | null>(null);
    const toolbarRef = useRef<ToolbarWithSaveHandle>(null);

    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);

    useEffect(() => {
        if (code) {
            setIsLoading(true);
            authService
                .get<StatnoViewModel>(`/settingsApi/GetStatNo?code=${code}`)
                .then((response) =>
                    setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null)
                )
                .catch((error: any) => {
                    showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
                })
                .finally(() => setIsLoading(false));
        } else {
            const defaultModel: StatnoViewModel = {
                code: "",
                description: "",
                isCreating: true,
                originalValues: {},
                hasChanges: false,
            };
            defaultModel.originalValues = { ...defaultModel };
            setViewModel(defaultModel);
        }
    }, [code, authService]);

    const handleSave = async () => {
        if (!viewModel) return;
        if (!validate()) return;
        const newItem = viewModel.isCreating ?? false;
        setIsLoading(true);
        try {
            const response = await authService.post<StatnoViewModel>("/settingsApi/CreateOrUpdateStatNo", viewModel);
            setViewModel({ ...response, originalValues: { ...response }, hasChanges: false });
            showSnackbar(t("dialog.savedSuccessfully"), "success");
            if (newItem) {
                navigate({ path: "/settings/statno/edit", params: { code: response.code } });
            }
        } catch (error: any) {
            showSnackbar(`${t("dialog.saveErrorGeneric")}: ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (field: keyof StatnoViewModel, value: any) => {
        setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
    };

    const handleCancelChanges = () => {
        setViewModel((prev) => {
            if (!prev || !prev.originalValues) return prev;
            const original = prev.originalValues as StatnoViewModel;
            return { ...original, originalValues: { ...original }, hasChanges: false };
        });
    };

    const handleDelete = async () => {
        if (!viewModel) return;
        if (viewModel.hasChanges) toolbarRef.current?.showHasChangesError();
        const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
        if (!isConfirmed) return;
        if (viewModel.isCreating) {
            navigate({ path: "/settings/statno/list" });
            return;
        }
        setIsLoading(true);
        try {
            await authService.post(`/settingsApi/DeleteStatNo`, viewModel);
            showSnackbar(t("dialog.deletedSuccessfully"), "success");
            navigate({ path: "/settings/statno/list" });
        } catch (error: any) {
            showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const validate = () => {
        if (!viewModel) {
            showSnackbar(t("dialog.dataMissing"), "error");
            return false;
        }
        if (!viewModel.code || viewModel.code.trim() === "") {
            showSnackbar(t("help.codeRequired"), "warning");
            return false;
        }
        if (!viewModel.description || viewModel.description.trim() === "") {
            showSnackbar(t("help.descriptionRequired"), "warning");
            return false;
        }
        return true;
    };

    return (
        <CenteredCard
            ref={toolbarRef}
            title={t("common.edit") + " " + t("common.statNo")}
            isLoading={isLoading}
            handleSave={handleSave}
            handleCancel={handleCancelChanges}
            hasChanges={viewModel?.hasChanges ?? false}
        >
            <Box sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                maxWidth: "fit-content",
                mb: 2
            }}>
                <DefaultActions
                    onSave={handleSave}
                    onDelete={handleDelete}
             
                />
            </Box>
            {viewModel && (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label={t("common.code")}
                            value={viewModel.code || ""}
                            onChange={(e) => handleChange("code", e.target.value)}
                            fullWidth
                            margin="normal"
                            disabled={viewModel.isCreating != true}
                            error={!viewModel.code || viewModel.code.trim() === ""}
                            helperText={!viewModel.code || viewModel.code.trim() === "" ? t("help.codeRequired") : ""}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={t("common.description")}
                            value={viewModel.description || ""}
                            onChange={(e) => handleChange("description", e.target.value)}
                            fullWidth
                            margin="normal"
                            error={!viewModel.description || viewModel.description.trim() === ""}
                            helperText={!viewModel.description || viewModel.description.trim() === "" ? t("help.descriptionRequired") : ""}
                        />
                    </Grid>
                </Grid>
            )}
        </CenteredCard>
    );
};

export default StatnoEditPage;
